import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { QCard, QCardContent, QTextField, QTypography, QButton, QAutocomplete } from '@kw/quokka-ui';
import { SettingsMode } from '../enum/SettingsMode.enum';
import { sendHttpRequest } from '../../../utils/network.service';
import { MutatorOptions } from 'swr';
import { useFetchService } from '../../../utils/fetchService';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { Clear, ExpandMore } from '@mui/icons-material';

interface HomeCountryConfigurationCardCardProps {
  isAdmin: boolean;
  currentHomeCountry?: string;
  mutate: (data?: any, opts?: boolean | MutatorOptions<any>) => Promise<any>;
}

export const HomeCountryConfigurationCard = ({ isAdmin, currentHomeCountry, mutate }: HomeCountryConfigurationCardCardProps) => {
  const [mode, setMode] = useState(SettingsMode.READ);
  const isEditMode = mode === SettingsMode.EDIT;
  const [homeCountryCode, setHomeCountryCode] = useState(currentHomeCountry || 'US');
  const [countriesDataOptions, setCountriesDataOptions] = useState<{ label: string; value: string }[]>([
    { label: 'United States of America (the)', value: 'US' },
  ]);

  const { enqueueSnackbar } = useSnackbar();

  type CountryType = {
    name: string;
    alpha2_code: string;
    alpha3_code: string;
    numeric_code: string;
  };

  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { data, error: countriesError } = useFetchService<CountryType[]>('/list/countryCodes', accessToken);

  const resetInputValues = () => {
    setHomeCountryCode(currentHomeCountry || 'US');
    setMode(SettingsMode.READ);
    mutate();
  };

  useEffect(() => {
    if (data == null || countriesError) return;
    const options = data
      .map(country => ({
        label: country.name,
        value: country.alpha2_code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setCountriesDataOptions(options);
  }, [data]);

  const updateHomeCountry = async () => {
    const body = { homeCountryCode: homeCountryCode };
    await sendHttpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/`,
      method: 'PUT',
      body,
      successMessage: 'Successfully updated Q-Scout home country',
      errorMessage: 'Error updating Q-Scout home country',
      enqueueSnackbar,
    });
    setMode(SettingsMode.READ);
    mutate();
  };

  const getCountryNameFromCode = code => {
    return countriesDataOptions.find(country => country.value === code)?.label || 'NOT SET';
  };

  return (
    <StyledQCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold">Home Country Configuration</StyledQTypography>
        <StyledQTypography variant="bodyRegular">
          Configure the home country for your Organization. This will be used to determine which network connections in app analysis are
          considered foreign connections.
        </StyledQTypography>
        <StyledQTypography variant="h5Medium">Home Country</StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <QAutocomplete
              data-testid="home-country-list"
              noOptionsText="nothing found"
              onChange={(_, selection) => setHomeCountryCode(selection?.value ?? 'US')}
              value={getCountryNameFromCode(homeCountryCode)}
              options={countriesDataOptions}
              isOptionEqualToValue={(option, value) => option.value === value}
              disableClearable
              renderInput={params => (
                <QTextField
                  {...params}
                  outlinedWithTitle
                  size="small"
                  variant="outlined"
                  style={{ textOverflow: 'ellipsis' }}
                  InputProps={{
                    endAdornment: params.InputProps.endAdornment,
                    sx: {
                      '.MuiAutocomplete-endAdornment': {
                        position: 'absolute',
                      },
                    },
                    inputProps: params.inputProps,
                    ref: params.InputProps.ref,
                  }}
                />
              )}
              popupIcon={<ExpandMore fontSize="small" />}
              clearIcon={<Clear fontSize="small" />}
            />
            <StyledButtonWrapper>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateHomeCountry}>
                Save
              </QButton>
            </StyledButtonWrapper>
          </>
        ) : (
          <>
            <StyledQTypography>{getCountryNameFromCode(homeCountryCode)}</StyledQTypography>
            <StyledButtonWrapper>
              <QButton variant="filled" onClick={() => setMode(SettingsMode.EDIT)}>
                Edit
              </QButton>
            </StyledButtonWrapper>
          </>
        )}
      </QCardContent>
    </StyledQCard>
  );
};

const StyledQCard = styled(QCard)`
  width: 100%;
  min-width: 300px;
  max-width: 400px;
`;

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;

const StyledButtonWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  column-gap: 14px;
  margin-top: 24px;
`;
